import { Check } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { PT_PER_MM, type DisplayedBox } from './placement-math';
import { round1, type Field } from './place-image-box';

/**
 * A measure typed as text: shown with the language's decimal separator («73,5» in Italian),
 * accepting either separator, and committed only once it reads as a number, so «12,» or an empty
 * field never moves the image.
 */
function NumberField({
  id,
  value,
  unit,
  decimals,
  onCommit,
}: {
  readonly id: string;
  readonly value: number;
  readonly unit: string;
  readonly decimals: number;
  readonly onCommit: (value: number) => void;
}) {
  const { i18n } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  const shown = new Intl.NumberFormat(i18n.language, {
    maximumFractionDigits: decimals,
    useGrouping: false,
  }).format(value);
  return (
    <span className="flex items-center rounded-md border border-input bg-background pr-2.5">
      <input
        id={id}
        type="text"
        inputMode="decimal"
        value={draft ?? shown}
        onChange={(e) => {
          const text = e.target.value;
          setDraft(text);
          const normalised = text.trim().replace(',', '.');
          if (/^-?\d+(\.\d+)?$/.test(normalised)) onCommit(Number(normalised));
        }}
        onBlur={() => setDraft(null)}
        className="h-9 w-full min-w-0 bg-transparent px-2.5 text-sm text-foreground tabular-nums outline-none"
      />
      <span>{unit}</span>
    </span>
  );
}

// The fields show the image box's top-left corner from the page's top-left, in millimetres.
const mm = (pt: number): number => round1(pt / PT_PER_MM);

function MeasureFields({
  box,
  onField,
}: {
  readonly box: DisplayedBox;
  readonly onField: (field: Field, value: number) => void;
}) {
  const { t } = useTranslation('tools');
  const uid = useId();
  return (
    <div className="hidden grid-cols-2 gap-2.5 sm:grid">
      {(
        [
          ['x', 'x', mm(box.cx - box.width / 2)],
          ['y', 'y', mm(box.cy - box.height / 2)],
          ['w', 'width', mm(box.width)],
          ['h', 'height', mm(box.height)],
        ] as const
      ).map(([field, label, value]) => (
        <label
          key={field}
          htmlFor={`${uid}-${field}`}
          className="flex flex-col gap-1 text-xs text-muted-foreground"
        >
          {t(`pdf.place.${label}`)}
          <NumberField
            id={`${uid}-${field}`}
            value={value}
            unit="mm"
            decimals={1}
            onCommit={(v) => onField(field, v)}
          />
        </label>
      ))}
      <label
        htmlFor={`${uid}-angle`}
        className="col-span-2 flex flex-col gap-1 text-xs text-muted-foreground"
      >
        {t('pdf.place.rotation')}
        <NumberField
          id={`${uid}-angle`}
          value={Math.round(box.angle)}
          unit="°"
          decimals={0}
          onCommit={(v) => onField('angle', v)}
        />
      </label>
    </div>
  );
}

export interface PlacePanelProps {
  readonly imageName: string;
  readonly box: DisplayedBox;
  readonly keepRatio: boolean;
  readonly onKeepRatio: (keep: boolean) => void;
  readonly onField: (field: Field, value: number) => void;
  readonly onCancel: () => void;
  readonly onPlace: () => void;
}

/** The side panel: the image's name, exact measures (desktop), the ratio lock and the buttons. */
export function PlacePanel(p: PlacePanelProps) {
  const { t } = useTranslation('tools');
  const uid = useId();
  return (
    <div className="flex flex-col gap-3.5 sm:w-64">
      <span className="hidden text-xs font-medium tracking-wide text-muted-foreground uppercase sm:block">
        {p.imageName}
      </span>
      <p className="hidden text-sm text-muted-foreground sm:block">{t('pdf.place.help')}</p>
      <MeasureFields box={p.box} onField={p.onField} />
      <label htmlFor={`${uid}-ratio`} className="flex items-center gap-2 text-sm">
        <input
          id={`${uid}-ratio`}
          type="checkbox"
          checked={p.keepRatio}
          onChange={(e) => p.onKeepRatio(e.target.checked)}
          className="size-4"
        />
        {t('pdf.place.keepRatio')}
      </label>
      <div className="flex gap-2 sm:mt-2">
        <Button
          type="button"
          variant="outline"
          className="h-12 grow sm:h-10 sm:grow-0"
          onClick={p.onCancel}
        >
          {t('pdf.place.cancel')}
        </Button>
        <Button type="button" className="h-12 grow-[2] sm:h-10 sm:grow-0" onClick={p.onPlace}>
          <Check aria-hidden="true" />
          <span className="sm:hidden">{t('pdf.place.done')}</span>
          <span className="hidden sm:inline">{t('pdf.place.insert')}</span>
        </Button>
      </div>
    </div>
  );
}
